import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { fetchMe } from "@/lib/authApi";

/**
 * 3-state ProtectedRoute (null = checking, true = authed, false = not authed).
 * Skips the /auth/me round-trip if AuthCallback already passed the user via
 * location.state — avoiding the race condition described in the playbook.
 */
export default function ProtectedRoute({ children }) {
    const location = useLocation();
    const [authState, setAuthState] = useState(
        location.state?.user ? { ok: true, user: location.state.user } : null
    );

    useEffect(() => {
        if (authState !== null) return;

        // If we just landed back from the OAuth callback, AuthCallback will handle it.
        if (window.location.hash?.includes("session_id=")) {
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const user = await fetchMe();
                if (!cancelled) setAuthState({ ok: true, user });
            } catch (_) {
                if (!cancelled) setAuthState({ ok: false });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authState]);

    if (authState === null) {
        return (
            <div className="min-h-screen grid place-items-center bg-white">
                <div className="inline-flex items-center gap-3 text-[12.5px] tracking-[0.22em] uppercase text-slate-500">
                    <span className="inline-block w-2 h-2 bg-[#B45309] animate-pulse" />
                    Checking session…
                </div>
            </div>
        );
    }

    if (!authState.ok) {
        return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
    }

    // Logged in but not on the admin allow-list → bounce to login with a flag
    // so the login page can surface a polite refusal rather than rendering the
    // admin chrome around a 403 banner.
    if (authState.user && authState.user.is_admin === false) {
        return (
            <Navigate
                to="/admin/login"
                replace
                state={{ refused: true, email: authState.user.email }}
            />
        );
    }

    // Inject user into the rendered child via React.cloneElement if it accepts `user` prop
    return typeof children === "function" ? children(authState.user) : children;
}
